<div class="container">
  <form class="card-form" [formGroup]="tierForm" (ngSubmit)="submitForm()" autocomplete="off" novalidate>
    <div class="blue-top">
      <span class="blue-top-title">{{ isStudentCover ? 'Student Personal Accident Details' : 'Personal Accident Details' }}</span>
    </div>

    <div class="card-section top-section">
      <h1><b>{{ isStudentCover ? 'Cover Benefits' : pageHeading }}</b></h1>
      <p class="page-subtitle" *ngIf="!isStudentCover">Select the cover you wish to take</p>

      <!-- ── Tier dropdown (hidden for student cover — fixed single tier) ──── -->
      <mat-form-field appearance="outline" class="form-select" *ngIf="!isStudentCover">
        <mat-label class="form-select-placeholder-unselected">{{ tierSelectLabel }}</mat-label>
        <mat-select formControlName="selectedTier" panelClass="form-select-popup">
          <mat-option class="form-option" *ngFor="let tier of tierEntries; trackBy: trackByTierKey" [value]="tier.key">
            Option {{ tier.key }} ({{ tier.name }})
          </mat-option>
        </mat-select>
        <mat-error *ngIf="tierForm.controls.selectedTier.touched && tierForm.controls.selectedTier.invalid">
          Please select a tier
        </mat-error>
      </mat-form-field>

      <!-- ── Benefits card: loading ─────────────────────────────────────────── -->
      <div class="benefits-card benefits-card--loading" *ngIf="isLoadingBenefit">
        <mat-progress-spinner mode="indeterminate" diameter="32"></mat-progress-spinner>
        <p class="card-loading-text">Loading cover benefits…</p>
      </div>

      <!-- ── Benefits card: error ───────────────────────────────────────────── -->
      <div class="benefits-card benefits-card--error" *ngIf="!isLoadingBenefit && benefitLoadError">
        <p class="card-error-message">{{ benefitLoadError }}</p>
        <button mat-button type="button" class="retry-button" (click)="retryLoadBenefits()">
          Retry
        </button>
      </div>

      <!-- ── Benefits card: data ────────────────────────────────────────────── -->
      <div class="benefits-card" *ngIf="!isLoadingBenefit && !benefitLoadError && hasBenefits">
        <div class="option-title" *ngIf="!isStudentCover">Option {{ selectedTierKey }} ({{ tierName }})</div>
        <div *ngIf="isPreviewBenefits && !isStudentCover" class="preview-note">
          Select your occupation below to see your specific premium.
        </div>
        <div class="option-amount" *ngIf="!isStudentCover">KES {{ displayRawBenefits[0].BenefitLimit | number:'1.0-0' }}</div>

        <button type="button" class="toggle-benefits" (click)="toggleCoverBenefits()">
          {{ coverBenefitsHidden ? 'Show Cover benefits' : 'Hide Cover benefits' }}
        </button>

        <div *ngIf="!coverBenefitsHidden">
          <div class="detail-row" *ngFor="let b of displayRawBenefits">
            <span>{{ b.BenefitDescription }}</span>
            <strong>KES {{ b.BenefitLimit | number:'1.0-0' }}</strong>
          </div>
        </div>
      </div>

      <div class="section-divider"></div>

      <h1><b>Get Quote</b></h1>
      <p class="page-subtitle">Fill in details below to get your quote</p>

      <!-- ── Occupation (hidden for student cover) ──────────────────────────── -->
      <mat-form-field appearance="outline" class="form-select" *ngIf="!isStudentCover">
        <mat-label class="form-select-placeholder-unselected">What is your occupation</mat-label>
        <input matInput formControlName="occupation" [matAutocomplete]="occupationAuto" placeholder="Type to search...">
        <mat-autocomplete #occupationAuto="matAutocomplete" panelClass="occupation-autocomplete-panel">
          <mat-option *ngFor="let occ of filteredOccupations" [value]="occ">
            {{ occ }}
          </mat-option>
        </mat-autocomplete>
        <mat-error *ngIf="tierForm.controls.occupation.touched && tierForm.controls.occupation.invalid">
          Please select your occupation
        </mat-error>
      </mat-form-field>

      <!-- ── Cover start date ───────────────────────────────────────────────── -->
      <mat-form-field appearance="outline" class="form-select">
        <mat-label class="form-select-placeholder-unselected">Enter the date you would like the cover to start</mat-label>
        <input matInput formControlName="coverStartDate" [min]="minCoverDate" [max]="maxCoverDate"
               [matDatepicker]="coverStartPicker" readonly="true" (click)="coverStartPicker.open()">
        <mat-datepicker-toggle matSuffix [for]="coverStartPicker"></mat-datepicker-toggle>
        <mat-datepicker #coverStartPicker></mat-datepicker>
        <mat-error *ngIf="tierForm.controls.coverStartDate.touched && tierForm.controls.coverStartDate.hasError('required')">
          Cover start date is required
        </mat-error>
        <mat-error *ngIf="tierForm.controls.coverStartDate.touched && tierForm.controls.coverStartDate.hasError('invalidCoverStartDate')">
          Please select a valid cover start date
        </mat-error>
      </mat-form-field>

      <!-- ── "Other" occupation contact card ───────────────────────────────── -->
      <div class="other-occupation-card" *ngIf="!isStudentCover && isOtherOccupationSelected">
        <div class="other-occupation-title">Unfortunately, we do not cover your occupation at this time.</div>
        <div class="other-occupation-subtitle">Please reach out to our Customer Care team for assistance:</div>
        <div class="other-occupation-line">📞 Call: +254 719 071999 or +254 730 151000</div>
        <div class="other-occupation-line">📧 Email: contactcentre@icealion.com or insurance@icealion.com</div>
        <div class="other-occupation-line">💬 WhatsApp: 020 38 92 300</div>
        <div class="other-occupation-line">📱 Mobile: *793#</div>
      </div>

      <!-- ── Enhanced benefits (non-student only) ───────────────────────────── -->
      <div class="enhanced-benefits-card modified_checkbox" *ngIf="!isStudentCover">
        <div class="enhanced-benefits-title">Enhanced Benefits</div>
        <p class="enhanced-benefits-subtitle">Please select the benefits that you would like to add to the cover</p>

        <mat-checkbox formControlName="excelsior">
          Excelsior (includes Optical, Dental and Evacuations)
        </mat-checkbox>

        <!-- All Excelsior states wrapped in a single guard -->
        <ng-container *ngIf="tierForm.controls.excelsior.value">

          <!-- Excelsior: loading -->
          <div class="excelsior-benefits-card card-inline-loading" *ngIf="isLoadingExcelsior">
            <mat-progress-spinner mode="indeterminate" diameter="24"></mat-progress-spinner>
            <span class="card-loading-text">Loading Excelsior benefits…</span>
          </div>

          <!-- Excelsior: error -->
          <div class="excelsior-benefits-card card-inline-error" *ngIf="!isLoadingExcelsior && excelsiorLoadError">
            <p class="card-error-message">{{ excelsiorLoadError }}</p>
            <button mat-button type="button" class="retry-button" (click)="retryLoadExcelsiorBenefits()">
              Retry
            </button>
          </div>

          <!-- Excelsior: data -->
          <div class="excelsior-benefits-card"
               *ngIf="!isLoadingExcelsior && !excelsiorLoadError && hasExcelsiorBenefits">
            <div class="excelsior-benefits-header">
              <span class="excelsior-benefits-title">Excelsior Cover — Option {{ selectedTierKey }} ({{ tierName }})</span>
              <button type="button" class="toggle-benefits" (click)="toggleExcelsiorBenefits()">
                {{ excelsiorBenefitsHidden ? 'Show Excelsior benefits' : 'Hide Excelsior benefits' }}
              </button>
            </div>
            <div *ngIf="!excelsiorBenefitsHidden">
              <div class="detail-row" *ngFor="let b of currentExcelsiorRawBenefits">
                <span>{{ b.BenefitDescription }}</span>
                <strong>KES {{ b.BenefitLimit | number:'1.0-0' }}</strong>
              </div>
            </div>
          </div>

        </ng-container>

        <mat-checkbox formControlName="passiveWar">
          PVT coverage
        </mat-checkbox>
      </div>

      <!-- ── Next of Kin Details ────────────────────────────────────────────── -->
      <div class="section-divider"></div>
      <h2 class="section-heading"><b>Next of Kin Details</b></h2>

      <div formArrayName="nextOfKin">
        <div *ngFor="let kin of nextOfKin.controls; let i = index" [formGroupName]="i" class="kin-card">

          <div class="kin-card-header" *ngIf="nextOfKin.length > 1">
            <span class="kin-index">Next of Kin {{ i + 1 }}</span>
            <button type="button" class="remove-kin-btn" (click)="removeNextOfKin(i)">Remove</button>
          </div>

          <mat-form-field appearance="outline" class="form-select">
            <mat-label>Full Name</mat-label>
            <input matInput formControlName="fullName">
            <mat-error *ngIf="kin.get('fullName')?.touched && kin.get('fullName')?.invalid">
              Full name is required
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="form-select">
            <mat-label>Relationship</mat-label>
            <mat-select formControlName="relationship">
              <mat-option *ngFor="let r of relationships" [value]="r">{{ r }}</mat-option>
            </mat-select>
            <mat-error *ngIf="kin.get('relationship')?.touched && kin.get('relationship')?.invalid">
              Relationship is required
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="form-select">
            <mat-label>Email address</mat-label>
            <input matInput formControlName="emailAddress" type="email">
            <mat-error *ngIf="kin.get('emailAddress')?.touched && kin.get('emailAddress')?.hasError('required')">
              Email address is required
            </mat-error>
            <mat-error *ngIf="kin.get('emailAddress')?.touched && kin.get('emailAddress')?.hasError('email')">
              Please enter a valid email address
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="form-select">
            <mat-label>Phone number</mat-label>
            <input matInput formControlName="phoneNumber" placeholder="07...">
            <mat-error *ngIf="kin.get('phoneNumber')?.touched && kin.get('phoneNumber')?.hasError('required')">
              Phone number is required
            </mat-error>
            <mat-error *ngIf="kin.get('phoneNumber')?.touched && kin.get('phoneNumber')?.hasError('pattern')">
              Please enter a valid phone number e.g. 07XX XXXXXX
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="form-select">
            <mat-label>Share %</mat-label>
            <input matInput formControlName="sharePercent" type="number" min="1" max="100">
            <mat-hint>Proportion of the benefit payable to the next of kin. All shares must total 100%.</mat-hint>
            <mat-error *ngIf="kin.get('sharePercent')?.touched && kin.get('sharePercent')?.invalid">
              Share percentage is required (1–100)
            </mat-error>
          </mat-form-field>

          <div class="section-divider" *ngIf="i < nextOfKin.length - 1"></div>
        </div>
      </div>

      <div class="share-error" *ngIf="sharePercentError">
        Total share percentage must equal 100%. Currently: {{ totalSharePercent }}%
      </div>

      <button type="button" class="add-kin-btn" (click)="addNextOfKin()">
        + Add another Next of Kin
      </button>

      <!-- ── Quote summary ──────────────────────────────────────────────────── -->
      <!--
        Show once standard premium is available AND excelsior (if on) is also loaded.
        Passive war loading is handled inline inside the card so existing premiums
        remain visible while the war component is being fetched from ESB.
      -->
      <div class="premium-amount-card" *ngIf="annualPremium !== null && !isLoadingExcelsior">
        <div class="premium-summary-title">Quote Summary</div>

        <div class="premium-row" *ngIf="!tierForm.controls.excelsior.value">
          <span class="premium-label">{{ isStudentCover ? 'Premium' : 'Occupation Premium' }}</span>
          <strong class="premium-value">KES {{ annualPremium | number:'1.0-0' }}</strong>
        </div>

        <div class="premium-row" *ngIf="excelsiorAnnualPremium !== null">
          <span class="premium-label">Excelsior Premium</span>
          <strong class="premium-value">KES {{ excelsiorAnnualPremium | number:'1.0-0' }}</strong>
        </div>

        <div class="premium-row premium-total-row" *ngIf="subtotalPremium !== null">
          <span class="premium-label">Subtotal Premium</span>
          <strong class="premium-value premium-total-value">KES {{ subtotalPremium | number:'1.0-0' }}</strong>
        </div>

        <!-- PVT premium: one row, either the spinner while ESB fetches warY or the resolved amount -->
        <div class="premium-row premium-war-loading" *ngIf="isLoadingPassiveWar || (!passiveWarLoadError && passiveWarPremium !== null)">
          <span class="premium-label">PVT Premium</span>
          <mat-progress-spinner *ngIf="isLoadingPassiveWar" mode="indeterminate" diameter="20"></mat-progress-spinner>
          <strong class="premium-value" *ngIf="!isLoadingPassiveWar">KES {{ passiveWarPremium | number:'1.0-0' }}</strong>
        </div>

        <!-- Passive war: error -->
        <div class="premium-row premium-war-error" *ngIf="!isLoadingPassiveWar && passiveWarLoadError">
          <span class="premium-label" style="color: #c0392b;">{{ passiveWarLoadError }}</span>
          <button mat-button type="button" class="retry-button retry-button--small" (click)="retryLoadPassiveWarPremiums()">
            Retry
          </button>
        </div>

        <div class="premium-row premium-total-row" *ngIf="!isLoadingPassiveWar && totalPremium !== null">
          <span class="premium-label">Total Premium (Payable)</span>
          <strong class="premium-value premium-total-value">KES {{ totalPremium | number:'1.0-0' }}</strong>
        </div>
      </div>

      <!-- ── Submit ──────────────────────────────────────────────────────────── -->
      <div class="row-div">
        <button mat-button type="submit" class="save-continue-button"
                [disabled]="tierForm.invalid || isOtherOccupationSelected || isSubmitting || isLoadingBenefit || isLoadingExcelsior || isLoadingPassiveWar || !hasBenefits || totalSharePercent !== 100">
          <span class="button-text">{{ isSubmitting ? 'Submitting...' : saveContinue }}</span>
        </button>
      </div>
    </div>
  </form>
</div>
